import type { z } from 'zod';

import type { EnvelopeMeta } from './http';

/**
 * D-17: "Zod validation wired manually … required on every endpoint."
 *
 * The base query already stripped the envelope, so the schema here is the
 * *payload* schema straight from `@saniplus/contracts` — never a local
 * re-declaration. A response that does not match throws, which surfaces as an
 * RTK Query error rather than as an undefined field three components deep.
 */
export function zodTransform<S extends z.ZodType>(schema: S): (raw: unknown) => z.output<S> {
  return (raw: unknown) => schema.parse(raw);
}

/**
 * The one-liner convention. Every injected endpoint spreads this instead of
 * hand-writing `transformResponse`:
 *
 * ```ts
 * getMe: build.query<MeResponse, void>({
 *   query: () => ({ url: '/auth/me' }),
 *   ...zodResponse(MeResponseSchema),
 *   providesTags: ['Session'],
 * }),
 * ```
 */
export function zodResponse<S extends z.ZodType>(
  schema: S,
): { transformResponse: (raw: unknown) => z.output<S> } {
  return { transformResponse: zodTransform(schema) };
}

/** A page of results plus the envelope's pagination meta. */
export interface Paginated<T> {
  readonly items: T[];
  readonly page: number;
  readonly pageSize: number;
  readonly total: number;
  readonly totalPages: number;
}

/**
 * The paginated one-liner. `meta` is the envelope meta the base query passed
 * through, so `total` never has to be counted client-side.
 */
export function zodPaginatedResponse<S extends z.ZodType>(
  itemSchema: S,
): {
  transformResponse: (raw: unknown, meta: EnvelopeMeta | undefined) => Paginated<z.output<S>>;
} {
  return {
    transformResponse: (raw: unknown, meta: EnvelopeMeta | undefined) => {
      if (!Array.isArray(raw)) {
        throw new Error(
          `zodPaginatedResponse: expected an array payload, got ${typeof raw}. ` +
            'The endpoint regressed to a non-list shape.',
        );
      }
      const items = raw.map((item) => itemSchema.parse(item) as z.output<S>);
      return {
        items,
        page: meta?.page ?? 1,
        pageSize: meta?.pageSize ?? items.length,
        total: meta?.total ?? items.length,
        totalPages: meta?.totalPages ?? 1,
      };
    },
  };
}
